<template>
  <!--
    AB#21836 — where a desk message takes the screen. Mounted once in the app
    bar for anyone who may receive. Asks the backend on every route change and
    every five minutes whether one is due; shows it over a scrim; never over an
    open drawer or form (it waits and tries again); records what happened.
  -->
  <Teleport to="body">
    <div v-if="card" class="dmh" data-test="desk-message-host">
      <div class="dmh-scrim" aria-hidden="true"></div>
      <div class="dmh-center" role="dialog" aria-modal="true" :aria-label="card.headline">
        <DeskMessageCard
          :card="card"
          :when="when"
          @primary="onPrimary"
          @secondary="onSecondary"
          @order="onOrder"
          @hide-change="(v) => (hideToday = v)"
        />
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DeskMessageCard from './DeskMessageCard.vue';
import {
  deskOrderTarget,
  deskPrimaryTarget,
  fetchPending,
  goToDeskTarget,
  recordEvent,
  somethingIsOpen,
  type DeskCard,
  type DeskOrder,
} from '@/composables/useDeskMessages';

const props = withDefaults(defineProps<{ pollMs?: number; enabled?: boolean }>(), {
  pollMs: 300000,
  enabled: true,
});
const emit = defineEmits<{ (e: 'shown'): void; (e: 'closed'): void }>();

const route = useRoute();
const router = useRouter();
const card = ref<DeskCard | null>(null);
const when = ref('');
const hideToday = ref(false);
let shownAt = 0;
let poll: ReturnType<typeof setInterval> | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;

const check = async (): Promise<void> => {
  if (!props.enabled || card.value) return;
  const next = await fetchPending();
  if (!next) return;
  if (somethingIsOpen()) {
    // Not now. Record why, and look again shortly.
    void recordEvent(next, 'suppressed', { reason: 'form_open', context: { page: route.path } });
    if (retry) clearTimeout(retry);
    retry = setTimeout(check, 60000);
    return;
  }
  card.value = next;
  when.value = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  hideToday.value = false;
  shownAt = Date.now();
  void recordEvent(next, 'shown', {
    context: { page: route.path, metrics: next.metrics, card: next },
  });
  emit('shown');
};

const seconds = (): number => Math.round((Date.now() - shownAt) / 1000);

const close = (): void => {
  card.value = null;
  emit('closed');
};

const onPrimary = (primary: DeskCard['primary']): void => {
  if (!card.value) return;
  void recordEvent(card.value, 'acted', {
    context: { action: primary.label, seconds_to_click: seconds(), page: route.path },
  });
  const target = deskPrimaryTarget(primary);
  close();
  goToDeskTarget(router, route.name, target);
};

const onOrder = (o: DeskOrder): void => {
  if (!card.value) return;
  void recordEvent(card.value, 'acted', {
    context: { action: `order:${o.order_id}`, seconds_to_click: seconds(), page: route.path },
  });
  close();
  goToDeskTarget(router, route.name, deskOrderTarget(o));
};

const onSecondary = (action: 'later' | 'snooze'): void => {
  if (!card.value) return;
  if (action === 'snooze') {
    void recordEvent(card.value, 'snoozed', {
      context: { seconds_to_click: seconds(), page: route.path },
    });
  } else if (hideToday.value) {
    void recordEvent(card.value, 'hidden', {
      reason: 'checkbox',
      context: { seconds_to_click: seconds(), page: route.path },
    });
  } else {
    void recordEvent(card.value, 'later', {
      context: { seconds_to_click: seconds(), page: route.path },
    });
  }
  close();
};

watch(
  () => route.path,
  () => void check(),
  { immediate: true }
);
watch(
  () => props.enabled,
  (on) => {
    if (on && poll === null) poll = setInterval(check, props.pollMs);
    if (!on && poll !== null) {
      clearInterval(poll);
      poll = null;
    }
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  if (poll) clearInterval(poll);
  if (retry) clearTimeout(retry);
});

defineExpose({ check, close });
</script>

<style scoped>
.dmh-scrim {
  position: fixed;
  inset: 0;
  top: var(--app-bar-offset, 53px);
  background: rgba(10, 12, 16, 0.55);
  backdrop-filter: blur(2px);
  z-index: 10040;
}
.dmh-center {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -48%);
  z-index: 10041;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  border-radius: 16px;
  animation: dmh-in 0.22s ease-out;
}
@keyframes dmh-in {
  from {
    transform: translate(-50%, -44%);
    opacity: 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .dmh-center {
    animation: none;
  }
}
</style>
